export const REFERENCE_PROJECTS_DECISION_WITHOUT = 'WITHOUT_REFERENCE_PROJECTS' as const;
export const REFERENCE_PROJECTS_DECISION_WITH = 'WITH_REFERENCE_PROJECTS' as const;

export type ReferenceProjectsDecision =
    | typeof REFERENCE_PROJECTS_DECISION_WITHOUT
    | typeof REFERENCE_PROJECTS_DECISION_WITH;

export type ReferenceProjectsReply =
    | { decision: typeof REFERENCE_PROJECTS_DECISION_WITHOUT }
    | { decision: typeof REFERENCE_PROJECTS_DECISION_WITH; workIds: string[] };

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Parses a reference-projects user reply.
 *
 * Accepts the pilot's on-the-wire shape unchanged, so threads created before this library
 * existed still render. That shape carries a redundant `value` object alongside `decision` /
 * `workIds` (see `buildReply.ts` for why it is still sent); it is validated away here and never
 * reaches a component.
 *
 * `workIds` missing on a WITH reply yields `[]` rather than `null`. The pilot's message view read
 * `d.workIds ?? []`, so such a message rendered rather than failing, and tightening that now would
 * turn already-stored history into an `unparseable` bubble. A `workIds` that is present but not an
 * array of strings is still rejected.
 *
 * @param data - The input to parse (expected to be the user message data)
 * @returns A validated `ReferenceProjectsReply`, or `null` if validation fails
 */
export function parseReferenceProjectsReply(data: unknown): ReferenceProjectsReply | null {
    if (!isRecord(data)) {
        return null;
    }

    const { decision, workIds } = data;

    if (decision === REFERENCE_PROJECTS_DECISION_WITHOUT) {
        return { decision: REFERENCE_PROJECTS_DECISION_WITHOUT };
    }

    if (decision !== REFERENCE_PROJECTS_DECISION_WITH) {
        return null;
    }

    if (workIds === undefined) {
        return { decision: REFERENCE_PROJECTS_DECISION_WITH, workIds: [] };
    }

    if (!Array.isArray(workIds) || !workIds.every((id) => typeof id === 'string')) {
        return null;
    }

    return { decision: REFERENCE_PROJECTS_DECISION_WITH, workIds: workIds as string[] };
}
